import { useState, useEffect, useCallback } from 'react';
import { 
  initDB, 
  saveData, 
  getData, 
  STORES 
} from '../utils/indexedDB';
import { 
  isOnline, 
  queueOfflineOperation, 
  processPendingSyncs,
  setupOnlineListener,
  getLastSyncInfo
} from '../utils/offlineSync';

export function useOffline() {
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [dbReady, setDbReady] = useState(false);

  // Initialize IndexedDB
  useEffect(() => {
    initDB()
      .then(() => setDbReady(true))
      .catch(err => console.error('Failed to init IndexedDB:', err));
  }, []);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Setup sync listener and update pending count
  useEffect(() => {
    const updateSyncInfo = async () => {
      const info = await getLastSyncInfo();
      setPendingCount(info.pendingCount);
      setLastSync(info.lastSyncAttempt);
    };

    updateSyncInfo();

    const cleanup = setupOnlineListener(async (result) => {
      console.log('Sync completed:', result);
      await updateSyncInfo();
    });

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SYNC_PENDING_REQUESTS') {
          processPendingSyncs().then(updateSyncInfo);
        }
      });
    }

    return cleanup;
  }, []);

  // Manual sync trigger
  const syncNow = useCallback(async () => {
    if (!online) {
      return { success: false, message: 'Vous êtes hors ligne' };
    }

    setSyncing(true);
    try {
      const result = await processPendingSyncs();
      const info = await getLastSyncInfo();
      setPendingCount(info.pendingCount);
      setLastSync(info.lastSyncAttempt);
      return result;
    } finally {
      setSyncing(false);
    }
  }, [online]);

  // Queue operation for offline sync
  const queueOperation = useCallback(async (operation) => {
    await queueOfflineOperation(operation);
    const info = await getLastSyncInfo();
    setPendingCount(info.pendingCount);
  }, []);

  // Cache data locally
  const cacheData = useCallback(async (storeName, data) => {
    if (!dbReady) return false;
    try {
      await saveData(storeName, data);
      return true;
    } catch (error) {
      console.error('Failed to cache data:', error);
      return false;
    }
  }, [dbReady]);

  // Get cached data
  const getCachedData = useCallback(async (storeName, id = null) => {
    if (!dbReady) return null;
    try {
      return await getData(storeName, id);
    } catch (error) {
      console.error('Failed to get cached data:', error);
      return null;
    }
  }, [dbReady]);

  return {
    online,
    syncing,
    pendingCount,
    lastSync,
    dbReady,
    syncNow,
    queueOperation,
    cacheData,
    getCachedData,
    STORES
  };
}

export default useOffline;
